import { useEffect, useRef, useState } from 'react';
import imageLoader from '../utils/optimizedImageLoader';

interface UseOptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
  withCardOverlay?: boolean;
}

interface UseOptimizedImageReturn {
  imgRef: React.RefObject<HTMLImageElement>;
  isLoading: boolean;
  hasError: boolean;
  optimizedSrc: string;
  cardRef?: React.RefObject<HTMLDivElement>;
  overlayRef?: React.RefObject<HTMLDivElement>;
  loadingState?: { isLoading: boolean; progress: number; error: boolean };
}

export const useOptimizedImage = ({
  src,
  alt,
  className = '',
  onLoad,
  onError,
  withCardOverlay = false
}: UseOptimizedImageProps): UseOptimizedImageReturn => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [optimizedSrc, setOptimizedSrc] = useState(src);

  // Card overlay logic
  const cardRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [loadingState, setLoadingState] = useState({ isLoading: true, progress: 0, error: false });

  // --- Restore overlay helper functions ---
  const createOverlay = () => {
    const overlay = overlayRef.current!;
    overlay.style.position = 'absolute';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.background = 'linear-gradient(135deg, rgba(26, 26, 46, 0.95) 0%, rgba(22, 33, 62, 0.95) 50%, rgba(15, 52, 96, 0.95) 100%)';
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    overlay.style.justifyContent = 'center';
    overlay.style.alignItems = 'center';
    overlay.style.zIndex = '9999';
    setTimeout(() => {
      console.log('[Overlay Debug] Computed overlay z-index:', getComputedStyle(overlay).zIndex);
    }, 0);
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 0.3s ease-in-out';
    overlay.style.borderRadius = '15%';
    overlay.style.backdropFilter = 'blur(2px)';
    overlay.style.pointerEvents = 'none';
    overlay.style.border = '2px dashed red';
    console.log('[Overlay Debug] Overlay styles set:', overlay.style.cssText);
    // Create icon
    const icon = document.createElement('img');
    icon.src = '/img/AeLogo.ico';
    icon.style.width = '50px';
    icon.style.height = '50px';
    icon.style.marginTop = '20%';
    icon.style.animation = 'cardBounce 1.2s ease-in-out infinite, cardGlow 2s ease-in-out infinite';
    icon.style.filter = 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))';
    icon.style.borderRadius = '15%';
    icon.style.background = 'rgba(255, 255, 255, 0.1)';
    icon.style.padding = '8px';
    icon.alt = 'Loading...';
    setTimeout(() => {
      console.log('[Overlay Debug] AeLogo computed styles (after append):', getComputedStyle(icon));
    }, 0);
    // Create progress container
    const progressContainer = document.createElement('div');
    progressContainer.style.position = 'absolute';
    progressContainer.style.bottom = '10px';
    progressContainer.style.left = '10px';
    progressContainer.style.right = '10px';
    progressContainer.style.height = '3px';
    progressContainer.style.background = 'rgba(255, 255, 255, 0.2)';
    progressContainer.style.borderRadius = '2px';
    progressContainer.style.overflow = 'hidden';
    // Create progress fill
    const progressFill = document.createElement('div');
    progressFill.style.height = '100%';
    progressFill.style.background = 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)';
    progressFill.style.width = '0%';
    progressFill.style.transition = 'width 0.3s ease-out';
    progressFill.style.borderRadius = '2px';
    progressFill.style.boxShadow = '0 1px 4px rgba(102, 126, 234, 0.5)';
    // Create text
    const loadingText = document.createElement('div');
    loadingText.innerHTML = 'Loading...';
    loadingText.style.color = 'white';
    loadingText.style.fontFamily = 'Arial, sans-serif';
    loadingText.style.fontSize = '14px';
    loadingText.style.fontWeight = 'bold';
    loadingText.style.textAlign = 'center';
    loadingText.style.marginTop = '12px';
    loadingText.style.opacity = '0.95';
    loadingText.style.textShadow = '0 2px 4px rgba(0, 0, 0, 0.7)';
    loadingText.style.background = 'rgba(0, 0, 0, 0.3)';
    loadingText.style.padding = '4px 8px';
    loadingText.style.borderRadius = '4px';
    // Assemble overlay
    progressContainer.appendChild(progressFill);
    overlay.appendChild(icon);
    overlay.appendChild(progressContainer);
    overlay.appendChild(loadingText);
    console.log('[Overlay Debug] Overlay children appended:', overlay.childNodes);
    return { progressFill, loadingText };
  };
  const addAnimations = () => {
    // Animations are now in index.css, so no need to inject styles dynamically
    console.log('✅ Card loading animations available from CSS');
  };
  const showLoading = () => {
    const overlay = overlayRef.current;
    if (!overlay) {
      console.log('❌ No overlay for showLoading');
      return;
    }
    console.log('🎨 Showing card loading animation');
    overlay.style.opacity = '1';
    console.log('[Overlay Debug] Overlay opacity set to 1, should be visible now.');
    // Start progress animation
    let progress = 0;
    const progressFill = overlay.querySelector('div');
    const loadingText = overlay.querySelector('div + div');
    const interval = setInterval(() => {
      progress += Math.random() * 20;
      if (progress > 90) progress = 90;
      if (progressFill) {
        (progressFill as HTMLElement).style.width = `${progress}%`;
      }
      if (loadingText) {
        (loadingText as HTMLElement).innerHTML = `Loading... ${Math.round(progress)}%`;
      }
      setLoadingState(prev => ({ ...prev, progress: Math.round(progress) }));
      if (progress >= 90) {
        clearInterval(interval);
      }
    }, 300);
  };
  const hideLoading = () => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    // overlay.style.opacity = '0'; // No need to set opacity to 0
    setTimeout(() => {
      if (overlay && overlay.parentElement) {
        overlay.parentElement.removeChild(overlay);
        console.log('[Overlay Debug] Overlay removed from DOM.');
      }
    }, 300);
  };

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    // Reset states
    setIsLoading(true);
    setHasError(false);
    setOptimizedSrc(src);

    // Add loading class
    img.classList.add('image-loading');

    // Handle load event
    const handleLoad = () => {
      setIsLoading(false);
      img.classList.remove('image-loading');
      img.classList.add('image-loaded');
      onLoad?.();
    };

    // Handle error event
    const handleError = () => {
      setIsLoading(false);
      setHasError(true);
      img.classList.remove('image-loading');
      img.classList.add('image-error');
      onError?.();
    };

    // Add event listeners
    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);

    // Optimize the image
    imageLoader.optimizeReactImage(img);

    // Cleanup
    return () => {
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };
  }, [src, onLoad, onError]);

  // Card overlay effect (only if withCardOverlay is true)
  useEffect(() => {
    if (!withCardOverlay) return;
    setTimeout(() => {
      if (!cardRef.current) {
        console.log('❌ No card ref found');
        return;
      }
      const card = cardRef.current;
      const overlay = overlayRef.current;
      if (!overlay) {
        console.log('❌ No overlay ref found');
        return;
      }
      console.log('✅ Setting up card loading overlay');
      const { progressFill, loadingText } = createOverlay();
      addAnimations();
      showLoading();
      // Restore previous logic: use a separate window.Image for overlay timing
      const img = new window.Image();
      img.onload = () => {
        console.log('✅ Card image loaded');
        if (progressFill && progressFill.style) {
          progressFill.style.width = '100%';
        }
        if (loadingText && loadingText.innerHTML !== undefined) {
          loadingText.innerHTML = 'Loaded!';
          loadingText.style.color = '#90EE90';
        }
        setLoadingState({ isLoading: false, progress: 100, error: false });
        hideLoading(); // Remove overlay immediately
      };
      img.onerror = () => {
        console.log('❌ Card image failed to load');
        if (loadingText && loadingText.innerHTML !== undefined) {
          loadingText.innerHTML = 'Failed to load';
          loadingText.style.color = '#ff6b6b';
        }
        setLoadingState({ isLoading: false, progress: 0, error: true });
        hideLoading(); // Remove overlay immediately
      };
      // Add null check before setting src
      if (img && src) {
        img.src = src;
      }
    }, 50);
    return () => {
      const overlay = overlayRef.current;
      if (overlay && overlay.parentElement) {
        overlay.parentElement.removeChild(overlay);
      }
    };
  }, [src, withCardOverlay]);

  if (withCardOverlay) {
    return {
      imgRef,
      isLoading,
      hasError,
      optimizedSrc,
      cardRef,
      overlayRef,
      loadingState
    };
  }
  return {
    imgRef,
    isLoading,
    hasError,
    optimizedSrc
  };
}; 